import {
  Alert,
  Box,
  Chip,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
} from '@mui/material'
import { useRef } from 'react'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { firebase_functions } from '../../../common/firebaseConfig'
import Typography from '@mui/material/Typography'
import { styled } from '@mui/material/styles'
import InfoIcon from '@mui/icons-material/Info'
import ForkRightIcon from '@mui/icons-material/ForkRight'
import ExitStrategyDrawer from './ExitStrategyDrawer'
import ExitStrategyInputs from './exitStrategies/ExitStrategyInputs'
import { GreenChartTooltip } from '../../../common/components/GreenChartTooltip'
import { useBacktestContext } from '../contexts/BacktestContext'
import CollapsibleInputContainer from './CollapsibleInputContainer'
import dayjs from 'dayjs'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'
import getNested from 'lodash.get'
import setNestedValue from 'lodash.set'
import { SnackbarHelperContext } from '../../../contexts/SnackbarHelperContext'
import { useApplicationSuiteContext } from '../../../contexts/ApplicationSuiteProvider'
import { usePattern } from '../../../hooks/usePattern'
import { useHttpsCallable } from 'react-firebase-hooks/functions'
import { ISymbol } from '../../../common/components/dialogs/symbolSelectDialog/SymbolSelect'
import getContractSize from '../../../common/utils/getContractSize'
import { COMPOSITE_GATE_MESSAGE, compositeGateActive } from '../../../common/utils/composite'
import { getSymbolTickSize, getTickDecimals } from '../../../common/utils/symbolPricing'
import BacktestActionBar from './actionBars/BacktestActionBar'
import BacktestHeaderBar from './actionBars/BacktestHeaderBar'
import PatternSelect from '../../../components/form/PatternSelect'
import { PatternVersionSelector } from '../../../components/form/PatternVersionSelector'
import SymbolSelectManager, {
  ISymbolSelectManagerRef,
} from '../../../common/components/dialogs/symbolSelectDialog/SymbolSelectManager'
import ScheduleSelect from '../../../components/form/ScheduleSelect'
import { getClientTimeZone } from '../../../common/timezones'

const RequiredInputSettings = {
  executionSymbol: 'A symbol must be selected',
  patternId: 'A pattern must be selected',
  generateTrades: {
    tradeRatios: 'A risk/reward must be selected',
    incrementalStart: 'An incremental start must be selected',
    incrementalStop: 'An incremental stop must be selected',
    incrementalStep: 'An incremental step must be selected',
  },
}

// Mirrors the server-side block in initiateBacktest until trade volume is capped.
const INCREMENTAL_UNAVAILABLE_MESSAGE =
  'The incremental exit strategy is temporarily unavailable while we improve it. Please choose a different exit strategy.'

const StyledContainer = styled(Box)(({ theme }) => ({
  '& .MuiInputBase-root, & .MuiSelect-root': {
    backgroundColor: theme.palette.background.custom4,
  },
}))

const defaultBacktestMinDate = new Date(2000, 0, 1)

// Starting point when a user opts into a custom spread; they type over it.
const DEFAULT_CUSTOM_SPREAD_POINTS = 25

export default function BacktestInput() {
  const { setBacktestInputIsValid, backtestSettingInputData, setBacktestSettingInputData } =
    useBacktestContext()

  const { usermeta, symbols } = useApplicationSuiteContext()

  const { setSnackbarSettings } = useContext(SnackbarHelperContext)

  const [pattern] = usePattern(
    backtestSettingInputData.patternId,
    backtestSettingInputData.patternVersion,
    backtestSettingInputData.gcid
  )
  const [exitPattern] = usePattern(
    backtestSettingInputData.exitPatternId,
    backtestSettingInputData.exitPatternVersion,
    backtestSettingInputData.gcid
  )

  const [isExitStrategyDrawerOpen, setIsExitStrategyDrawerOpen] = useState(false)
  const [formErrors, setFormErrors] = useState<Partial<typeof RequiredInputSettings>>({})
  const [minDateForSymbol, setMinDateForSymbol] = useState<Date>(defaultBacktestMinDate)
  const [getMinDateForSymbol] = useHttpsCallable(firebase_functions, 'getMinDateForSymbol')

  const managerRef = useRef<ISymbolSelectManagerRef | null>(null)

  // Use a ref for formErrors so handleInputChange doesn't need it as a dependency
  const formErrorsRef = useRef(formErrors)
  formErrorsRef.current = formErrors

  // Local state buffers for numeric inputs to avoid context re-renders on every keystroke.
  // Values sync to context on blur.
  const [localInputs, setLocalInputs] = useState<Record<string, string>>({})

  // Initialize local inputs from context when context changes externally
  const contextInputKey = useMemo(
    () =>
      JSON.stringify({
        equity: backtestSettingInputData.analyzeTrades.equity,
        riskPerTrade: backtestSettingInputData.analyzeTrades.riskPerTrade,
        commissions: backtestSettingInputData.analyzeTrades.commissions,
        spread: backtestSettingInputData.analyzeTrades.spread,
        tradeLimit: backtestSettingInputData.analyzeTrades.tradeLimit,
        maxRisk: backtestSettingInputData.analyzeTrades.maxRisk,
        dailyTradeLimit: backtestSettingInputData.analyzeTrades.dailyTradeLimit,
        contractSize: backtestSettingInputData.analyzeTrades.contractSize,
        forceExitPeriodicity: backtestSettingInputData.analyzeTrades.forceExit?.time?.periodicity,
      }),
    [
      backtestSettingInputData.analyzeTrades.equity,
      backtestSettingInputData.analyzeTrades.riskPerTrade,
      backtestSettingInputData.analyzeTrades.commissions,
      backtestSettingInputData.analyzeTrades.spread,
      backtestSettingInputData.analyzeTrades.tradeLimit,
      backtestSettingInputData.analyzeTrades.maxRisk,
      backtestSettingInputData.analyzeTrades.dailyTradeLimit,
      backtestSettingInputData.analyzeTrades.contractSize,
      backtestSettingInputData.analyzeTrades.forceExit?.time?.periodicity,
    ]
  )

  useEffect(() => {
    setLocalInputs({})
  }, [contextInputKey])

  const getLocalOrContext = (localKey: string, contextValue: any) => {
    return localKey in localInputs ? localInputs[localKey] : contextValue
  }

  const handleLocalChange = (localKey: string, displayValue: string) => {
    setLocalInputs((prev) => ({ ...prev, [localKey]: displayValue }))
  }

  const handleLocalBlur = (name: string, localKey: string, parse: (v: string) => number) => {
    const displayValue = localInputs[localKey]
    if (displayValue !== undefined) {
      const parsed = parse(displayValue)
      if (!isNaN(parsed)) {
        handleInputChange(name, parsed)
      }
      setLocalInputs((prev) => {
        const next = { ...prev }
        delete next[localKey]
        return next
      })
    }
  }

  const handleInputChange = useCallback(
    (name: string, value: any) => {
      if (getNested(formErrorsRef.current, name)) {
        setFormErrors((prevState) => {
          const newState = { ...prevState }
          setNestedValue(newState, name, undefined)
          return newState
        })
      }

      setBacktestSettingInputData((prevState: any) => {
        const newState = { ...prevState }
        setNestedValue(newState, name, value)
        return newState
      })
    },
    [setBacktestSettingInputData]
  )

  useEffect(() => {
    if (backtestSettingInputData.executionSymbol) {
      getMinDateForSymbol(backtestSettingInputData.executionSymbol).then((minDate: any) => {
        if (minDate && minDate.data) {
          setMinDateForSymbol(new Date(minDate.data))
        } else {
          setMinDateForSymbol(defaultBacktestMinDate)
        }
      })
    }
  }, [backtestSettingInputData.executionSymbol, getMinDateForSymbol])

  // The backtest engine can't evaluate custom (composite) indicators yet, and
  // the dialog has its own pattern pickers plus a Rerun path — so the run
  // itself is gated here, not just the dialog openers.
  const hasCompositePattern = useMemo(
    () =>
      (!!pattern && compositeGateActive(pattern)) ||
      (!!exitPattern && compositeGateActive(exitPattern)),
    [pattern, exitPattern]
  )

  useEffect(() => {
    if (backtestSettingInputData) {
      setBacktestInputIsValid(!getInvalidInputs().length && !hasCompositePattern)
    }

    validateInput()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backtestSettingInputData, hasCompositePattern])

  useEffect(() => {
    if (pattern && pattern.version) {
      setBacktestSettingInputData((prevState: any) => {
        const newState = { ...prevState }
        setNestedValue(newState, 'patternVersion', pattern.version)
        return newState
      })
    }
  }, [pattern, setBacktestSettingInputData])

  function validateIncrementalCount(start: number, stop: number, step: number): boolean {
    if (!start || !stop || !step) return true
    const incrementals = Math.abs(start - stop) / step
    return incrementals <= 10
  }

  function getInvalidInputs() {
    return [
      { type: 'exists', key: 'executionSymbol', message: RequiredInputSettings.executionSymbol },
      {
        type: 'exists',
        key: 'generateTrades.tradeRatios',
        message: RequiredInputSettings.generateTrades.tradeRatios,
      },
      { type: 'exists', key: 'patternId', message: RequiredInputSettings.patternId },
      {
        type: 'validation',
        key: 'generateTrades.tradeRatios',
        isValid: (value: string[]) => value.length <= 15,
        message: 'Max 15 ratios allowed.',
      },
      {
        type: 'validation',
        key: 'generateTrades.exitStrategy',
        isValid: (value: any) => value !== 'incremental',
        message: INCREMENTAL_UNAVAILABLE_MESSAGE,
      },
      {
        type: 'validation',
        key: 'generateTrades.incrementalStart',
        isValid: (value: number) =>
          backtestSettingInputData.generateTrades.exitStrategy !== 'incremental' ||
          validateIncrementalCount(
            value,
            backtestSettingInputData.generateTrades.incrementalStop,
            backtestSettingInputData.generateTrades.incrementalStep
          ),
        message: 'Too many incrementals. Maximum 10 incrementals allowed.',
      },
      {
        type: 'validation',
        key: 'generateTrades.incrementalStop',
        isValid: (value: number) =>
          backtestSettingInputData.generateTrades.exitStrategy !== 'incremental' ||
          validateIncrementalCount(
            backtestSettingInputData.generateTrades.incrementalStart,
            value,
            backtestSettingInputData.generateTrades.incrementalStep
          ),
        message: 'Too many incrementals. Maximum 10 incrementals allowed.',
      },
      {
        type: 'validation',
        key: 'generateTrades.incrementalStep',
        isValid: (value: number) =>
          backtestSettingInputData.generateTrades.exitStrategy !== 'incremental' ||
          validateIncrementalCount(
            backtestSettingInputData.generateTrades.incrementalStart,
            backtestSettingInputData.generateTrades.incrementalStop,
            value
          ),
        message: 'Too many incrementals. Maximum 10 incrementals allowed.',
      },
      {
        type: 'validation',
        key: 'generateTrades.incrementalStart',
        isValid: (value: number) =>
          backtestSettingInputData.generateTrades.exitStrategy !== 'incremental' ||
          value > backtestSettingInputData.generateTrades.incrementalStop,
        message: 'Incremental start must be greater than incremental stop.',
      },
      {
        type: 'validation',
        key: 'generateTrades.incrementalStop',
        isValid: (value: number) =>
          backtestSettingInputData.generateTrades.exitStrategy !== 'incremental' ||
          value < backtestSettingInputData.generateTrades.incrementalStart,
        message: 'Incremental stop must be less than incremental start.',
      },
    ].filter((validation) => {
      if (validation.type === 'exists') {
        return (
          !getNested(backtestSettingInputData, validation.key) ||
          (Array.isArray(getNested(backtestSettingInputData, validation.key)) &&
            getNested(backtestSettingInputData, validation.key).length === 0)
        )
      } else if (validation.type === 'validation' && validation.isValid) {
        return !validation.isValid(getNested(backtestSettingInputData, validation.key))
      }
      return false
    })
  }

  function validateInput() {
    const invalidInputs = getInvalidInputs()

    const newErrors: any = {}
    invalidInputs.forEach((validation) => {
      setNestedValue(newErrors, validation.key, validation.message)
    })
    setFormErrors(newErrors)

    return !invalidInputs.length
  }

  const handleDateInputChange = (name: string, value: number) => {
    let newDate = new Date(value)

    // Validates that the date is not before the min date or after the current date
    if (newDate < minDateForSymbol) {
      newDate = minDateForSymbol
    } else if (newDate > new Date()) {
      newDate = new Date()
    }

    // Sets the time to 00:00:00:000 for the start date and 23:59:59:999 for the end date
    if (name === 'dateRange.startDate') {
      newDate.setHours(0, 0, 0, 0)
    } else if (name === 'dateRange.endDate') {
      newDate.setHours(23, 59, 59, 999)
    }

    // Validates that the start date is not after the end date and vice versa
    if (
      name === 'dateRange.startDate' &&
      newDate.getTime() >= backtestSettingInputData.dateRange.endDate
    ) {
      const newEndDate = new Date(newDate)
      newEndDate.setHours(23, 59, 59, 999)

      handleInputChange('dateRange.endDate', newEndDate.getTime())
      setSnackbarSettings({
        severity: 'warning',
        message: 'End date adjusted as start date cannot be after end date.',
        autoHideDuration: 6000,
      })
    } else if (
      name === 'dateRange.endDate' &&
      newDate.getTime() <= backtestSettingInputData.dateRange.startDate
    ) {
      const newStartDate = new Date(newDate)
      newStartDate.setHours(0, 0, 0, 0)
      handleInputChange('dateRange.startDate', newStartDate.getTime())
      setSnackbarSettings({
        severity: 'warning',
        message: 'Start date adjusted as end date cannot be before start date.',
        autoHideDuration: 6000,
      })
    }

    handleInputChange(name, newDate.getTime())
  }

  const selectSymbol = (symbolChosen: ISymbol | undefined) => {
    // Handle case where symbol is cleared (empty name)
    if (!symbolChosen) {
      handleInputChange('executionSymbol', undefined)
      return
    }

    handleInputChange('executionSymbol', symbolChosen.name)
    const symbolObject = symbols?.find((symbol: any) => symbol.name === symbolChosen.name)
    if (symbolObject) {
      handleInputChange('analyzeTrades.contractSize', getContractSize(symbolObject))
    }
  }

  const patternStrategySubTitle = (
    <>
      <Box display="flex" gap={1} alignItems={'center'}>
        <Box display={'flex'} alignItems={'center'} gap={1}>
          <b>Pattern:</b>{' '}
          {pattern ? (
            <Typography variant={'body1'}>
              {pattern.name} <Typography variant={'caption'}>(v{pattern.version})</Typography>
            </Typography>
          ) : (
            <b style={{ color: 'rgb(201, 80, 80)' }}>None selected</b>
          )}
        </Box>
        <Box>
          <b>Symbol:</b>{' '}
          {backtestSettingInputData.executionSymbol ? (
            backtestSettingInputData.executionSymbol
          ) : (
            <b style={{ color: 'rgb(201, 80, 80)' }}>None selected</b>
          )}
        </Box>
        <Box>
          <b>Date range:</b>{' '}
          {new Date(backtestSettingInputData.dateRange.startDate).toLocaleDateString()} -{' '}
          {new Date(backtestSettingInputData.dateRange.endDate).toLocaleDateString()}
        </Box>
        <Box>
          <b>Trigger:</b>{' '}
          {pattern &&
            `${pattern.trigger.chartDetails.periodicity} ${pattern.trigger.chartDetails.timeframe}`}{' '}
          at post
        </Box>
      </Box>
    </>
  )

  const spreadPoints = backtestSettingInputData.analyzeTrades.spread
  const isCustomSpread = spreadPoints !== null && spreadPoints !== undefined

  // Spread is entered in points so it survives a symbol change; show what that
  // currently means in price for the selected symbol. A point is the symbol's
  // tick size, not 10^-decimals — those differ on every index.
  const spreadPriceLabel = useMemo(() => {
    const points = Number(spreadPoints)
    if (!isCustomSpread || !Number.isFinite(points)) return ''
    const symbol = symbols?.find(
      (s: ISymbol) => s.name === backtestSettingInputData.executionSymbol
    )
    const tickSize = getSymbolTickSize(symbol)
    const priceOffset = points * tickSize
    // Render at the tick's own precision so 25 pts on EURUSD reads 0.00025, not 0.00.
    return `= ${priceOffset.toFixed(getTickDecimals(tickSize))}`
  }, [isCustomSpread, spreadPoints, symbols, backtestSettingInputData.executionSymbol])

  const accountDetailsSubTitle = (
    <>
      <Box display="flex" gap={1} alignItems={'center'}>
        <Box>
          <b>Equity:</b> ${backtestSettingInputData.analyzeTrades.equity}
        </Box>
        <Box>
          <b>Risk per trade:</b>{' '}
          {(backtestSettingInputData.analyzeTrades.riskPerTrade * 100).toFixed(2)}%
        </Box>
        <Box>
          <b>Commission:</b> ${backtestSettingInputData.analyzeTrades.commissions}
        </Box>
        <Box>
          <b>Spread:</b> {isCustomSpread ? `${spreadPoints} pts` : 'Standard'}
        </Box>
        <Box>
          <b>Max open trades:</b>{' '}
          {backtestSettingInputData.analyzeTrades.tradeLimit
            ? backtestSettingInputData.analyzeTrades.tradeLimit
            : '∞'}
        </Box>
        <Box>
          <b>Max Risk:</b>{' '}
          {backtestSettingInputData.analyzeTrades.maxRisk
            ? (backtestSettingInputData.analyzeTrades.maxRisk * 100).toFixed(2)
            : '∞'}
        </Box>
        <Box>
          <b>Daily trade limit:</b>{' '}
          {backtestSettingInputData.analyzeTrades.dailyTradeLimit
            ? backtestSettingInputData.analyzeTrades.dailyTradeLimit
            : '∞'}
        </Box>
        <Box>
          <b>Contract Size:</b>{' '}
          {backtestSettingInputData.analyzeTrades.contractSize
            ? backtestSettingInputData.analyzeTrades.contractSize
            : '1'}
        </Box>
      </Box>
    </>
  )

  const stopLossSubTitle = (
    <>
      <Box display="flex" gap={1} alignItems={'center'}>
        <Box>
          <b>Stop Loss:</b>
          {backtestSettingInputData.generateTrades.exitStrategy === 'peak_valley' ? ' Fractal' : ''}
          {backtestSettingInputData.generateTrades.exitStrategy !== 'peak_valley' &&
          backtestSettingInputData.generateTrades.exitStrategy !== 'high_low'
            ? ` ${backtestSettingInputData.generateTrades.exitStrategy[0].toUpperCase() + backtestSettingInputData.generateTrades.exitStrategy.slice(1)}`
            : ''}
          {backtestSettingInputData.generateTrades.exitStrategy === 'high_low' ? ' High/Low' : ''}
          {backtestSettingInputData.generateTrades.exitStrategy === 'atr'
            ? ` (${backtestSettingInputData.generateTrades.atrChartPeriod} ${backtestSettingInputData.generateTrades.atrChartTimeframe} ${backtestSettingInputData.generateTrades.atrPeriod} period)`
            : ''}
          {backtestSettingInputData.generateTrades.exitStrategy === 'incremental' ? (
            formErrors.generateTrades?.incrementalStart ||
            formErrors.generateTrades?.incrementalStop ||
            formErrors.generateTrades?.incrementalStep ? (
              <b style={{ color: 'rgb(201, 80, 80)' }}>
                {' '}
                ({backtestSettingInputData.generateTrades.incrementalStart} -{' '}
                {backtestSettingInputData.generateTrades.incrementalStop}, step:{' '}
                {backtestSettingInputData.generateTrades.incrementalStep})
              </b>
            ) : (
              ` (${backtestSettingInputData.generateTrades.incrementalStart} - ${backtestSettingInputData.generateTrades.incrementalStop}, step: ${backtestSettingInputData.generateTrades.incrementalStep})`
            )
          ) : (
            ''
          )}
        </Box>
        {backtestSettingInputData.generateTrades.exitStrategy === 'peak_valley'
          ? ` (${backtestSettingInputData.generateTrades.fractalCount} left/right, ${backtestSettingInputData.generateTrades.maxPeakValleyLookback} max lookback, ${backtestSettingInputData.generateTrades.priceOffset} price offset)`
          : ''}
        {backtestSettingInputData.generateTrades.exitStrategy === 'high_low'
          ? ` (${backtestSettingInputData.generateTrades.maxLookback} max lookback, ${backtestSettingInputData.generateTrades.priceOffset} price offset)`
          : ''}
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={0.5} mt={0.5}>
          <Typography variant="body2" fontWeight="bold" mr={0.5}>
            Trade Ratios:
          </Typography>
          {backtestSettingInputData.generateTrades.tradeRatios.length ? (
            backtestSettingInputData.generateTrades.tradeRatios.map((ratio: any, idx: number) => (
              <Chip
                key={idx}
                size="small"
                variant="outlined"
                color={formErrors.generateTrades?.tradeRatios ? 'error' : 'default'}
                label={
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <span>
                      {ratio.risk}/{ratio.reward}
                    </span>
                    {ratio.conditions?.length > 0 && (
                      <>
                        <span>·</span>
                        <Box display="flex" alignItems="center">
                          <span>{ratio.conditions.length}</span>
                          <ForkRightIcon sx={{ fontSize: 14 }} />
                        </Box>
                      </>
                    )}
                  </Box>
                }
              />
            ))
          ) : (
            <Typography variant="body2" fontWeight="bold" color="error">
              None selected
            </Typography>
          )}
        </Box>
      </Box>
    </>
  )

  const stopLossTitle = (
    <>
      <Box display={'flex'} gap={1} alignItems={'center'}>
        <Typography variant="h5">Stop Loss</Typography>
        <InfoIcon
          sx={{
            '&:hover': {
              cursor: 'pointer',
              color: '#00A1FF',
            },
          }}
          fontSize={'small'}
          onClick={(event) => {
            event.stopPropagation()
            setIsExitStrategyDrawerOpen(true)
          }}
        />
      </Box>
    </>
  )

  const tradeExitSubTitle = (
    <>
      <Box display="flex" gap={1} alignItems={'center'}>
        <Box>
          <b>Exit Pattern:</b>{' '}
          {exitPattern ? `${exitPattern.name} (v${exitPattern.version})` : 'None'}
        </Box>
        <Box>
          <b>Trigger:</b>{' '}
          {exitPattern
            ? `${exitPattern.trigger.chartDetails.periodicity} ${exitPattern.trigger.chartDetails.timeframe} at post`
            : 'None'}
        </Box>
        <Box>
          <b>Force exit on time elapsed:</b>{' '}
          {backtestSettingInputData.analyzeTrades.forceExit?.time
            ? `After ${backtestSettingInputData.analyzeTrades.forceExit.time.periodicity} ${backtestSettingInputData.analyzeTrades.forceExit.time.timeframe}${backtestSettingInputData.analyzeTrades.forceExit.time.periodicity > 1 ? 's' : ''}`
            : 'Off'}
        </Box>
      </Box>
    </>
  )

  return (
    <>
      <Box
        display={'flex'}
        flexDirection={'column'}
        width={'100%'}
        data-tour-id="backtest-run-dialog"
      >
        <BacktestHeaderBar />

        <StyledContainer sx={{ py: 2, px: 2, overflowY: 'auto' }}>
          {/* Persistent inline gate (not a toast): explains the disabled run
              while a selected pattern uses custom indicators */}
          {hasCompositePattern && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {COMPOSITE_GATE_MESSAGE}
            </Alert>
          )}
          <Box component="form">
            <CollapsibleInputContainer
              title={<Typography variant={'h5'}>Trade Entry</Typography>}
              defaultOpen={true}
              subTitle={patternStrategySubTitle}
            >
              <Grid container spacing={2} flexDirection={'column'}>
                <Grid container item spacing={2}>
                  <Grid item lg={6} sm={12}>
                    <Grid container spacing={2}>
                      <Grid item lg={6} sm={6} xs={12}>
                        <PatternSelect
                          pattern={pattern}
                          onSelect={(pattern) => {
                            handleInputChange('gcid', usermeta.gcid)
                            handleInputChange('patternId', pattern?.id || null)
                            handleInputChange('patternVersion', pattern?.version || null)
                          }}
                          error={{
                            hasError: !!formErrors.patternId,
                            message: formErrors.patternId,
                          }}
                        />
                        <PatternVersionSelector
                          patternId={backtestSettingInputData.patternId}
                          gcid={usermeta.gcid}
                          tradingHouse={backtestSettingInputData.pattern?.tradingHouse}
                          currentVersion={pattern?.version}
                          selectedVersion={backtestSettingInputData.patternVersion}
                          onVersionSelect={(version) => {
                            handleInputChange('patternVersion', version || pattern?.version)
                          }}
                        />
                      </Grid>
                      <Grid item lg={6} sm={6} xs={12} style={{ position: 'relative' }}>
                        <>
                          <SymbolSelectManager
                            ref={managerRef}
                            caller={'backtester'}
                            symbolSelectCallback={selectSymbol}
                            symbols={(symbols || []) as ISymbol[]}
                            currentSymbol={backtestSettingInputData.executionSymbol}
                          />
                          <FormControl variant="outlined" fullWidth>
                            <InputLabel id="symbol" error={!!formErrors.executionSymbol}>
                              Symbol
                            </InputLabel>
                            <Select
                              labelId="symbol"
                              value={backtestSettingInputData.executionSymbol || ''}
                              name="executionSymbol"
                              label="Symbol"
                              open={false}
                              onClick={() => {
                                if (managerRef.current) {
                                  managerRef.current.openDialog()
                                }
                              }}
                              error={!!formErrors.executionSymbol}
                            >
                              {(symbols || []).map((symbol: any, index) => (
                                <MenuItem key={index} value={symbol.name}>
                                  {symbol.name}
                                </MenuItem>
                              ))}
                            </Select>
                            {formErrors.executionSymbol && (
                              <FormHelperText error>{formErrors.executionSymbol}</FormHelperText>
                            )}
                          </FormControl>
                        </>
                      </Grid>
                      <Grid item lg={6} sm={6} xs={12}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DesktopDatePicker
                            sx={{ width: '100%' }}
                            label="Backtest start date"
                            value={dayjs(new Date(backtestSettingInputData.dateRange.startDate))}
                            minDate={dayjs(new Date(minDateForSymbol))}
                            maxDate={dayjs()}
                            onChange={(value: any) =>
                              handleDateInputChange('dateRange.startDate', value.$d.getTime())
                            }
                          />
                        </LocalizationProvider>
                      </Grid>
                      <Grid item lg={6} sm={6} xs={12}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DesktopDatePicker
                            sx={{ width: '100%' }}
                            label="Backtest end date"
                            value={dayjs(new Date(backtestSettingInputData.dateRange.endDate))}
                            minDate={dayjs(new Date(minDateForSymbol))}
                            maxDate={dayjs()}
                            onChange={(value: any) =>
                              handleDateInputChange('dateRange.endDate', value.$d.getTime())
                            }
                          />
                        </LocalizationProvider>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item lg={6} sm={12}>
                    <Grid container spacing={2}>
                      <Grid
                        item
                        lg={12}
                        sm={12}
                        xs={12}
                        display={'flex'}
                        alignItems={'center'}
                        justifyContent={'center'}
                      >
                        <Box width={'100%'}>
                          <ScheduleSelect
                            backtestMode={true}
                            timezone={
                              backtestSettingInputData.schedule?.timezone ?? getClientTimeZone()
                            }
                            windows={backtestSettingInputData.schedule?.windows ?? []}
                            onWindowsChange={(windows) => {
                              const viewerTz = getClientTimeZone()
                              setBacktestSettingInputData((prev: any) => ({
                                ...prev,
                                schedule: { timezone: viewerTz, windows },
                              }))
                            }}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </CollapsibleInputContainer>

            <CollapsibleInputContainer
              title={<Typography variant={'h5'}>Account Details</Typography>}
              subTitle={accountDetailsSubTitle}
            >
              <Grid container spacing={2} flexDirection={'column'}>
                <Grid container item spacing={2}>
                  <Grid item lg={3} md={4} sm={6} xs={12}>
                    <FormControl fullWidth>
                      <InputLabel htmlFor="outlined-adornment-amount">Equity</InputLabel>
                      <OutlinedInput
                        value={getLocalOrContext(
                          'equity',
                          backtestSettingInputData.analyzeTrades.equity
                        )}
                        name="analyzeTrades.equity"
                        onChange={(e) => handleLocalChange('equity', e.target.value)}
                        onBlur={() => handleLocalBlur('analyzeTrades.equity', 'equity', parseFloat)}
                        type={'number'}
                        inputProps={{ min: 0 }}
                        id="outlined-adornment-amount"
                        startAdornment={<InputAdornment position="start">$</InputAdornment>}
                        label="Equity"
                      />
                    </FormControl>
                  </Grid>
                  <Grid item lg={3} md={4} sm={6} xs={12}>
                    <FormControl fullWidth>
                      <InputLabel htmlFor="outlined-adornment-amount">Risk per trade</InputLabel>
                      <OutlinedInput
                        value={getLocalOrContext(
                          'riskPerTrade',
                          (backtestSettingInputData.analyzeTrades.riskPerTrade * 100).toFixed(2)
                        )}
                        name="analyzeTrades.riskPerTrade"
                        onChange={(e) => handleLocalChange('riskPerTrade', e.target.value)}
                        onBlur={() =>
                          handleLocalBlur(
                            'analyzeTrades.riskPerTrade',
                            'riskPerTrade',
                            (v) => parseFloat(v) / 100
                          )
                        }
                        type={'number'}
                        inputProps={{ step: 0.5, min: 0.5 }}
                        id="outlined-adornment-amount"
                        startAdornment={<InputAdornment position="start">%</InputAdornment>}
                        label="Risk per trade"
                      />
                    </FormControl>
                  </Grid>
                  <Grid item lg={3} md={4} sm={6} xs={12}>
                    <FormControl fullWidth>
                      <InputLabel htmlFor="outlined-adornment-amount">Commissions</InputLabel>
                      <OutlinedInput
                        value={getLocalOrContext(
                          'commissions',
                          backtestSettingInputData.analyzeTrades.commissions
                        )}
                        name="analyzeTrades.commissions"
                        onChange={(e) => handleLocalChange('commissions', e.target.value)}
                        onBlur={() =>
                          handleLocalBlur('analyzeTrades.commissions', 'commissions', parseFloat)
                        }
                        type={'number'}
                        inputProps={{ min: 0 }}
                        id="outlined-adornment-amount"
                        startAdornment={<InputAdornment position="start">$</InputAdornment>}
                        label="Commissions"
                      />
                    </FormControl>
                  </Grid>

                  <Grid item lg={3} md={4} sm={6} xs={12}>
                    <FormControl fullWidth>
                      <InputLabel htmlFor="outlined-adornment-spread">Spread</InputLabel>
                      <OutlinedInput
                        id="outlined-adornment-spread"
                        value={
                          isCustomSpread ? getLocalOrContext('spread', spreadPoints ?? '') : ''
                        }
                        name="analyzeTrades.spread"
                        disabled={!isCustomSpread}
                        placeholder="Standard"
                        onChange={(e) => handleLocalChange('spread', e.target.value)}
                        onBlur={() => handleLocalBlur('analyzeTrades.spread', 'spread', parseFloat)}
                        type={'number'}
                        inputProps={{ min: 0 }}
                        startAdornment={<InputAdornment position="start">pts</InputAdornment>}
                        label="Spread"
                        endAdornment={
                          <InputAdornment position="end">
                            <GreenChartTooltip
                              title={
                                <Box className="tooltip-text">
                                  {isCustomSpread ? 'Use standard spread' : 'Set a custom spread'}
                                </Box>
                              }
                              arrow
                            >
                              <span>
                                <Switch
                                  checked={isCustomSpread}
                                  onChange={(e) =>
                                    handleInputChange(
                                      'analyzeTrades.spread',
                                      e.target.checked ? DEFAULT_CUSTOM_SPREAD_POINTS : null
                                    )
                                  }
                                  color="primary"
                                />
                              </span>
                            </GreenChartTooltip>
                          </InputAdornment>
                        }
                      />
                      {spreadPriceLabel && <FormHelperText>{spreadPriceLabel}</FormHelperText>}
                    </FormControl>
                  </Grid>

                  <Grid item lg={3} md={4} sm={6} xs={12}>
                    <FormControl fullWidth>
                      <InputLabel htmlFor="outlined-adornment-amount">
                        Max open positions
                      </InputLabel>
                      <OutlinedInput
                        value={getLocalOrContext(
                          'tradeLimit',
                          backtestSettingInputData.analyzeTrades.tradeLimit === 0
                            ? ''
                            : backtestSettingInputData.analyzeTrades.tradeLimit
                        )}
                        name="analyzeTrades.tradeLimit"
                        onChange={(e) => handleLocalChange('tradeLimit', e.target.value)}
                        onBlur={() => {
                          const displayValue = localInputs['tradeLimit']
                          if (displayValue !== undefined) {
                            const parsed = parseInt(displayValue)
                            if (!isNaN(parsed)) {
                              if (parsed === 1) {
                                handleInputChange('analyzeTrades.allowHedging', false)
                              }
                              handleInputChange('analyzeTrades.tradeLimit', parsed)
                            }
                            setLocalInputs((prev) => {
                              const next = { ...prev }
                              delete next['tradeLimit']
                              return next
                            })
                          }
                        }}
                        type={'number'}
                        inputProps={{ min: 0 }}
                        id="outlined-adornment-amount"
                        startAdornment={<InputAdornment position="start">#</InputAdornment>}
                        label="Max open positions"
                        placeholder="∞"
                        endAdornment={
                          <InputAdornment position="end">
                            <GreenChartTooltip
                              title={
                                <Box className="tooltip-text">
                                  {backtestSettingInputData.analyzeTrades.tradeLimit === 1
                                    ? 'Change max open positions to enable hedging'
                                    : backtestSettingInputData.analyzeTrades.allowHedging
                                      ? 'Disable hedging'
                                      : 'Allow hedging'}
                                </Box>
                              }
                              arrow
                            >
                              <span>
                                <Switch
                                  disabled={backtestSettingInputData.analyzeTrades.tradeLimit === 1}
                                  checked={!!backtestSettingInputData.analyzeTrades.allowHedging}
                                  onChange={(e) =>
                                    handleInputChange(
                                      'analyzeTrades.allowHedging',
                                      e.target.checked
                                    )
                                  }
                                  color="primary"
                                />
                              </span>
                            </GreenChartTooltip>
                          </InputAdornment>
                        }
                      />
                    </FormControl>
                  </Grid>

                  <Grid item lg={3} md={4} sm={6} xs={12}>
                    <FormControl fullWidth>
                      <InputLabel htmlFor="outlined-adornment-amount">Max risk</InputLabel>
                      <OutlinedInput
                        value={getLocalOrContext(
                          'maxRisk',
                          backtestSettingInputData.analyzeTrades.maxRisk
                            ? backtestSettingInputData.analyzeTrades.maxRisk * 100
                            : ''
                        )}
                        placeholder="∞"
                        name="analyzeTrades.maxRisk"
                        onChange={(e) => handleLocalChange('maxRisk', e.target.value)}
                        onBlur={() =>
                          handleLocalBlur(
                            'analyzeTrades.maxRisk',
                            'maxRisk',
                            (v) => parseFloat(v) / 100
                          )
                        }
                        type={'number'}
                        id="outlined-adornment-amount"
                        startAdornment={<InputAdornment position="start">%</InputAdornment>}
                        label="Max risk"
                        inputProps={{ min: 0 }}
                      />
                    </FormControl>
                  </Grid>

                  <Grid item lg={3} md={4} sm={6} xs={12}>
                    <FormControl fullWidth>
                      <InputLabel htmlFor="outlined-adornment-amount">Daily trade limit</InputLabel>
                      <OutlinedInput
                        value={getLocalOrContext(
                          'dailyTradeLimit',
                          backtestSettingInputData.analyzeTrades.dailyTradeLimit === 0
                            ? ''
                            : backtestSettingInputData.analyzeTrades.dailyTradeLimit
                        )}
                        placeholder="∞"
                        name="analyzeTrades.dailyTradeLimit"
                        onChange={(e) => handleLocalChange('dailyTradeLimit', e.target.value)}
                        onBlur={() =>
                          handleLocalBlur(
                            'analyzeTrades.dailyTradeLimit',
                            'dailyTradeLimit',
                            parseInt
                          )
                        }
                        type={'number'}
                        id="outlined-adornment-amount"
                        startAdornment={<InputAdornment position="start">#</InputAdornment>}
                        label="Daily trade limit"
                        inputProps={{ min: 0 }}
                      />
                    </FormControl>
                  </Grid>

                  <Grid item lg={3} md={4} sm={6} xs={12}>
                    <FormControl fullWidth>
                      <InputLabel htmlFor="outlined-adornment-amount">Contract Size</InputLabel>
                      <OutlinedInput
                        value={getLocalOrContext(
                          'contractSize',
                          backtestSettingInputData.analyzeTrades.contractSize
                        )}
                        placeholder="∞"
                        name="analyzeTrades.contractSize"
                        onChange={(e) => handleLocalChange('contractSize', e.target.value)}
                        onBlur={() => {
                          const displayValue = localInputs['contractSize']
                          if (displayValue !== undefined) {
                            const parsed = parseInt(displayValue)
                            if (!isNaN(parsed)) {
                              const contractSizeMap =
                                JSON.parse(localStorage.getItem('contractSize') || '{}') || {}
                              contractSizeMap[backtestSettingInputData.executionSymbol] =
                                displayValue
                              localStorage.setItem('contractSize', JSON.stringify(contractSizeMap))
                              handleInputChange('analyzeTrades.contractSize', parsed)
                            }
                            setLocalInputs((prev) => {
                              const next = { ...prev }
                              delete next['contractSize']
                              return next
                            })
                          }
                        }}
                        type={'number'}
                        id="outlined-adornment-amount"
                        startAdornment={<InputAdornment position="start">#</InputAdornment>}
                        label="Contract Size"
                        inputProps={{ min: 1 }}
                      />
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
            </CollapsibleInputContainer>

            <CollapsibleInputContainer title={stopLossTitle} subTitle={stopLossSubTitle}>
              <Grid container spacing={2} flexDirection={'column'}>
                <Grid container item spacing={2}>
                  <Grid item lg={3} xs={12}>
                    <FormControl variant="outlined" fullWidth>
                      <InputLabel id="timeframe-label">Exit Strategy</InputLabel>
                      <Select
                        labelId="timeframe-label"
                        value={backtestSettingInputData.generateTrades.exitStrategy}
                        name="generateTrades.exitStrategy"
                        onChange={(e) => {
                          if (
                            (backtestSettingInputData.generateTrades.exitStrategy !== 'fixed' &&
                              e.target.value === 'fixed') ||
                            (backtestSettingInputData.generateTrades.exitStrategy === 'fixed' &&
                              e.target.value !== 'fixed')
                          ) {
                            handleInputChange('generateTrades.tradeRatios', [])
                          }
                          handleInputChange(e.target.name, e.target.value)
                        }}
                        label="Exit Strategy"
                      >
                        <MenuItem value="atr">ATR</MenuItem>
                        <MenuItem value="incremental" disabled>
                          Incremental (temporarily unavailable)
                        </MenuItem>
                        <MenuItem value="fixed">Fixed</MenuItem>
                        <MenuItem value="peak_valley">Fractal</MenuItem>
                        <MenuItem value="high_low">High/Low</MenuItem>
                      </Select>
                      {backtestSettingInputData.generateTrades.exitStrategy === 'incremental' && (
                        <FormHelperText error>{INCREMENTAL_UNAVAILABLE_MESSAGE}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>

                <Grid container item spacing={2}>
                  <ExitStrategyInputs
                    backtestSettingInputData={backtestSettingInputData}
                    handleInputChange={handleInputChange}
                    formErrors={formErrors}
                    multipleTradeRatios
                    periodicallyModifyTakeProfit
                  />
                </Grid>
              </Grid>
            </CollapsibleInputContainer>

            <CollapsibleInputContainer
              title={<Typography variant={'h5'}>Trade Exit</Typography>}
              subTitle={tradeExitSubTitle}
            >
              <Grid container spacing={2} flexDirection={'column'}>
                <Grid container item spacing={2}>
                  <Grid item lg={3} md={6} xs={12}>
                    <PatternSelect
                      pattern={exitPattern}
                      onSelect={(p) => {
                        if (!p) {
                          handleInputChange('exitPatternId', undefined)
                          handleInputChange('exitPatternVersion', undefined)
                          return
                        }
                        handleInputChange('exitPatternId', p.id)
                        handleInputChange('exitPatternVersion', p.version)
                      }}
                      helperText="Trades will close when this pattern is met."
                    />
                    {exitPattern && (
                      <PatternVersionSelector
                        patternId={backtestSettingInputData.exitPatternId}
                        gcid={usermeta.gcid}
                        tradingHouse={backtestSettingInputData.exitPattern?.tradingHouse}
                        currentVersion={exitPattern?.version}
                        selectedVersion={backtestSettingInputData.exitPatternVersion}
                        onVersionSelect={(version) => {
                          handleInputChange('exitPatternVersion', version || exitPattern?.version)
                        }}
                      />
                    )}
                  </Grid>
                  {exitPattern && (
                    <Grid item lg={3} md={6} xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Exit Pattern Mode</InputLabel>
                        <Select
                          value={backtestSettingInputData.exitPatternMode || 'both'}
                          onChange={(e) => handleInputChange('exitPatternMode', e.target.value)}
                          label="Exit Pattern Mode"
                        >
                          <MenuItem value="both">Both</MenuItem>
                          <MenuItem value="take_profit_only">Take Profit Only</MenuItem>
                          <MenuItem value="stop_loss_only">Stop Loss Only</MenuItem>
                        </Select>
                        <FormHelperText>
                          {backtestSettingInputData.exitPatternMode === 'take_profit_only'
                            ? 'Exit pattern is ignored when the trade is at a loss.'
                            : backtestSettingInputData.exitPatternMode === 'stop_loss_only'
                              ? 'Exit pattern is ignored when the trade is in profit.'
                              : 'Exit pattern triggers regardless of profit or loss.'}
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                  )}
                </Grid>

                <Grid container item spacing={2}>
                  <Grid item lg={3} xs={12} display={'flex'} alignItems={'center'}>
                    <Typography variant={'body1'}>Force exit on time elapsed</Typography>
                    <FormControl>
                      <Switch
                        checked={!!backtestSettingInputData.analyzeTrades.forceExit?.time}
                        onChange={(e) => {
                          const { time, ...otherForceExitProps } =
                            backtestSettingInputData.analyzeTrades.forceExit!
                          handleInputChange('analyzeTrades.forceExit', {
                            ...otherForceExitProps,
                            ...(e.target.checked
                              ? {
                                  time: {
                                    periodicity: 1,
                                    timeframe: 'minute',
                                  },
                                }
                              : {}),
                          })
                        }}
                        color="primary"
                      />
                    </FormControl>
                  </Grid>

                  <Grid container item xs={12} lg={9} spacing={2}>
                    {backtestSettingInputData.analyzeTrades.forceExit?.time ? (
                      <>
                        <Grid item lg={3} xs={12}>
                          <FormControl fullWidth>
                            <InputLabel>Force exit periodicity</InputLabel>
                            <OutlinedInput
                              value={getLocalOrContext(
                                'forceExitPeriodicity',
                                backtestSettingInputData.analyzeTrades.forceExit.time.periodicity
                              )}
                              name="analyzeTrades.forceExit.time.periodicity"
                              onChange={(e) =>
                                handleLocalChange('forceExitPeriodicity', e.target.value)
                              }
                              onBlur={() =>
                                handleLocalBlur(
                                  'analyzeTrades.forceExit.time.periodicity',
                                  'forceExitPeriodicity',
                                  parseFloat
                                )
                              }
                              type={'number'}
                              inputProps={{ min: 0 }}
                              label="Force exit periodicity"
                            />
                          </FormControl>
                        </Grid>
                        <Grid item lg={3} xs={12}>
                          <FormControl fullWidth>
                            <InputLabel id="timeframe-label">Force exit timeframe</InputLabel>
                            <Select
                              labelId="timeframe-label"
                              value={
                                backtestSettingInputData.analyzeTrades.forceExit.time.timeframe
                              }
                              name="analyzeTrades.forceExit.time.timeframe"
                              onChange={(e) => handleInputChange(e.target.name, e.target.value)}
                              label="Force exit timeframe"
                            >
                              <MenuItem value="minute">Minute</MenuItem>
                              <MenuItem value="hour">Hour</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </>
                    ) : null}
                  </Grid>
                </Grid>
              </Grid>
            </CollapsibleInputContainer>
          </Box>
        </StyledContainer>

        <Box flex="1" overflow="auto" />
        <BacktestActionBar />
      </Box>
      <ExitStrategyDrawer
        isOpen={isExitStrategyDrawerOpen}
        onClose={() => setIsExitStrategyDrawerOpen(false)}
      />
    </>
  )
}
